import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  ShoppingCart, Package, Search, Filter, Plus, Minus, Trash2,
  CreditCard, CheckCircle2, X, Store, Tag, TrendingUp,
  AlertCircle, Truck, Receipt, RefreshCw
} from 'lucide-react';
import { api } from '@/lib/api';

interface Product {
  id: number;
  name: string;
  sku: string;
  price: number;
  stock: number;
  category: string;
  description: string;
  isInStock: boolean;
}

interface CartItem {
  product: Product;
  quantity: number;
}

interface StoreSettings {
  storeName: string;
  storeDescription: string;
  taxRate: number;
  shippingFee: number;
  freeShippingThreshold: number;
  currency: string;
}

interface OrderTotals {
  subtotal: number;
  taxAmount: number;
  shippingFee: number;
  couponDiscount: number;
  totalAmount: number;
}

export default function StorePage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [settings, setSettings] = useState<StoreSettings | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [totals, setTotals] = useState<OrderTotals | null>(null);
  const [checkoutForm, setCheckoutForm] = useState({
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    shippingAddress: '',
    shippingCity: '',
    shippingArea: '',
    paymentMethod: 'cod',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    calculateTotals();
  }, [cart, couponCode]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes, settingsRes] = await Promise.all([
        api.get('/api/store/products'),
        api.get('/api/store/categories'),
        api.get('/api/store/settings')
      ]);

      if (productsRes.data.success) setProducts(productsRes.data.data);
      if (categoriesRes.data.success) setCategories(categoriesRes.data.data);
      if (settingsRes.data.success) setSettings(settingsRes.data.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = async () => {
    if (cart.length === 0) {
      setTotals(null);
      return;
    }
    try {
      const res = await api.post('/api/store/calculate', {
        items: cart.map(item => ({
          productId: item.product.id,
          quantity: item.quantity
        })),
        couponCode: couponCode || null
      });
      if (res.data.success) setTotals(res.data.data);
    } catch (error) {
      console.error('Error calculating totals:', error);
    }
  };

  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        if (existing.quantity >= product.stock) {
          alert('لا يوجد مخزون كافي');
          return prev;
        }
        return prev.map(item => 
          item.product.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const updateQuantity = (productId: number, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.product.id === productId) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) return item;
        if (newQty > item.product.stock) {
          alert('لا يوجد مخزون كافي');
          return item;
        }
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (productId: number) => {
    setCart(prev => prev.filter(item => item.product.id !== productId));
  };

  const createOrder = async () => {
    if (!checkoutForm.guestName || !checkoutForm.guestPhone || !checkoutForm.shippingAddress || !checkoutForm.shippingCity) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }
    try {
      const res = await api.post('/api/store/orders', {
        ...checkoutForm,
        items: cart.map(item => ({
          productId: item.product.id,
          quantity: item.quantity
        })),
        couponCode: couponCode || null
      });
      if (res.data.success) {
        setOrderSuccess(res.data.data.orderNumber);
        setCart([]);
        setCouponCode('');
        setShowCheckout(false);
        setShowCart(false);
      } else {
        alert(res.data.message);
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ');
    }
  };

  const filteredProducts = products.filter(p => {
    if (selectedCategory && p.category !== selectedCategory) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) &&
        !p.description.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const cartTotal = cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const formatCurrency = (value: number) => `${value.toLocaleString('ar-EG', { maximumFractionDigits: 2 })} ج.م`;

  return (
    <div className="p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
              <Store className="w-6 h-6 text-white" />
            </div>
            {settings?.storeName || 'المتجر'}
          </h1>
          <p className="text-muted-foreground mt-1">{settings?.storeDescription}</p>
        </div>
        <Button 
          onClick={() => setShowCart(true)} 
          className="relative bg-purple-600 hover:bg-purple-700 gap-2"
        >
          <ShoppingCart className="w-4 h-4" />
          السلة
          {cartCount > 0 && (
            <span className="absolute -top-2 -left-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
              {cartCount}
            </span>
          )}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="بحث عن منتج..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pr-10"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border rounded-lg bg-white"
            >
              <option value="">جميع الفئات</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <Button onClick={loadData} variant="outline">
              <RefreshCw className={`w-4 h-4 ml-2 ${loading ? 'animate-spin' : ''}`} />
              تحديث
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Products Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p>جاري التحميل...</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">لا توجد منتجات</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredProducts.map((product) => (
            <Card key={product.id} className="overflow-hidden hover:shadow-lg transition">
              <div className="h-40 bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                <Package className="w-20 h-20 text-purple-400" />
              </div>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bold text-lg line-clamp-1">{product.name}</h3>
                  {product.isInStock ? (
                    <Badge className="bg-green-100 text-green-700">متوفر</Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-700">نفد</Badge>
                  )}
                </div>
                <Badge variant="outline" className="mb-2">{product.category}</Badge>
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">{product.description}</p>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{formatCurrency(product.price)}</p>
                    <p className="text-xs text-gray-500">المخزون: {product.stock}</p>
                  </div>
                  <p className="text-xs text-gray-400 font-mono">{product.sku}</p>
                </div>
                <Button 
                  onClick={() => addToCart(product)}
                  disabled={!product.isInStock}
                  className="w-full bg-purple-600 hover:bg-purple-700 gap-2"
                >
                  <ShoppingCart className="w-4 h-4" />
                  أضف للسلة
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 bg-black/50 flex justify-start z-50">
          <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <ShoppingCart className="w-6 h-6 text-purple-600" />
                  سلة المشتريات
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCart(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">السلة فارغة</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-6">
                    {cart.map((item) => (
                      <Card key={item.product.id}>
                        <CardContent className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Package className="w-8 h-8 text-purple-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold line-clamp-1">{item.product.name}</h4>
                              <p className="text-sm text-purple-600 font-bold">
                                {formatCurrency(item.product.price)}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => updateQuantity(item.product.id, -1)}
                                  className="h-7 w-7 p-0"
                                >
                                  <Minus className="w-3 h-3" />
                                </Button>
                                <span className="font-semibold w-8 text-center">{item.quantity}</span>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => updateQuantity(item.product.id, 1)}
                                  disabled={item.quantity >= item.product.stock}
                                  className="h-7 w-7 p-0"
                                >
                                  <Plus className="w-3 h-3" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => removeFromCart(item.product.id)}
                                  className="h-7 w-7 p-0 text-red-600 mr-auto"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Coupon */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">كود الخصم</label>
                    <div className="flex gap-2">
                      <Input
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="أدخل الكود"
                        className="font-mono"
                      />
                      <Button variant="outline" onClick={calculateTotals}>
                        <Tag className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Totals */}
                  {totals && (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span>المجموع الفرعي:</span>
                        <span className="font-semibold">{formatCurrency(totals.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>الضريبة ({settings?.taxRate}%):</span>
                        <span className="font-semibold">{formatCurrency(totals.taxAmount)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>الشحن:</span>
                        <span className="font-semibold">
                          {totals.shippingFee === 0 ? 'مجاني' : formatCurrency(totals.shippingFee)}
                        </span>
                      </div>
                      {totals.couponDiscount > 0 && (
                        <div className="flex justify-between text-sm text-green-600">
                          <span>الخصم:</span>
                          <span className="font-semibold">- {formatCurrency(totals.couponDiscount)}</span>
                        </div>
                      )}
                      <div className="border-t pt-2 flex justify-between text-lg">
                        <span className="font-bold">الإجمالي:</span>
                        <span className="font-bold text-purple-600">{formatCurrency(totals.totalAmount)}</span>
                      </div>
                      {totals.subtotal < (settings?.freeShippingThreshold || 0) && (
                        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-700">
                          <Truck className="w-3 h-3 inline ml-1" />
                          أضف {formatCurrency((settings?.freeShippingThreshold || 0) - totals.subtotal)} للحصول على شحن مجاني
                        </div>
                      )}
                    </div>
                  )}

                  <Button 
                    onClick={() => setShowCheckout(true)}
                    className="w-full bg-purple-600 hover:bg-purple-700 gap-2"
                  >
                    <CreditCard className="w-4 h-4" />
                    إتمام الطلب
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-purple-500" />
                إتمام الطلب
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">الاسم *</label>
                  <Input
                    value={checkoutForm.guestName}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, guestName: e.target.value })}
                    placeholder="الاسم الكامل"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">الهاتف *</label>
                  <Input
                    value={checkoutForm.guestPhone}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, guestPhone: e.target.value })}
                    placeholder="01xxxxxxxxx"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">البريد الإلكتروني</label>
                <Input
                  type="email"
                  value={checkoutForm.guestEmail}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, guestEmail: e.target.value })}
                  placeholder="example@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">عنوان الشحن *</label>
                <Input
                  value={checkoutForm.shippingAddress}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, shippingAddress: e.target.value })}
                  placeholder="الشارع، رقم المبنى، الطابق..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">المدينة *</label>
                  <Input
                    value={checkoutForm.shippingCity}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, shippingCity: e.target.value })}
                    placeholder="القاهرة"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">المنطقة</label>
                  <Input
                    value={checkoutForm.shippingArea}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, shippingArea: e.target.value })}
                    placeholder="مدينة نصر"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">طريقة الدفع</label>
                <select
                  value={checkoutForm.paymentMethod}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, paymentMethod: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="cod">الدفع عند الاستلام</option>
                  <option value="fawry">Fawry</option>
                  <option value="paymob_card">بطاقة ائتمانية</option>
                  <option value="vodafone_cash">فودافون كاش</option>
                  <option value="instapay">إنستاباي</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">ملاحظات</label>
                <textarea
                  value={checkoutForm.notes}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, notes: e.target.value })}
                  placeholder="ملاحظات إضافية..."
                  className="w-full px-3 py-2 border rounded-lg min-h-[80px]"
                />
              </div>
              {totals && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>الإجمالي النهائي:</span>
                    <span className="text-purple-600">{formatCurrency(totals.totalAmount)}</span>
                  </div>
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <Button onClick={createOrder} className="flex-1 bg-purple-600 hover:bg-purple-700 gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  تأكيد الطلب
                </Button>
                <Button variant="outline" onClick={() => setShowCheckout(false)} className="flex-1">
                  إلغاء
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Order Success Modal */}
      {orderSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">تم الطلب بنجاح!</h2>
              <p className="text-gray-600 mb-4">رقم الطلب:</p>
              <p className="text-xl font-mono font-bold text-purple-600 mb-6">{orderSuccess}</p>
              <Button 
                onClick={() => setOrderSuccess(null)}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                إغلاق
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
